import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Text,
  Divider,
  useToast,
  Switch,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Icon,
  List,
  ListItem,
  ListIcon,
  Code,
  useColorModeValue
} from '@chakra-ui/react';
import { 
  MdSend, 
  MdEmail, 
  MdCheck, 
  MdClose, 
  MdInfo,
  MdRefresh,
  MdVisibility,
  MdCheckCircle,
  MdError,
  MdWarning
} from 'react-icons/md';
import { getToken } from '../../../../services/api';
import './EmailTest.css';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const EmailTest = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    to: '',
    additionalEmails: '',
    subject: '',
    templateId: 'custom',
    customHtml: '',
    variables: {
      nome: '',
      precatorios: '',
      conteudo_personalizado: '',
      valor: '',
      tipo_orgao_processo: '',
      valor_estimado: '',
      nome_empresa: '',
      razao_social: ''
    },
    ccAdmin: false
  });

  // SendGrid status
  const [sendGridStatus, setSendGridStatus] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [testHistory, setTestHistory] = useState([]);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    checkSendGridStatus();
    fetchTemplates();
  }, []);

  const checkSendGridStatus = async () => {
    try {
      setLoadingStatus(true);
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/email/test/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setSendGridStatus(data.status);
      }
    } catch (error) {
      console.error('Error checking SendGrid status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/email/templates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    if (name.startsWith('variables.')) {
      const varName = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        variables: {
          ...prev.variables,
          [varName]: value
        }
      }));
    } else {
      // If template is being changed, auto-load its subject
      if (name === 'templateId' && value !== 'custom') {
        const selectedTemplate = templates.find(t => t.id === value);
        let subject = selectedTemplate ? selectedTemplate.subject : formData.subject;
        
        // Replace placeholders in v5 subject
        if (value === 'precatorio-notification-v5' && subject) {
          subject = subject.replace('{{nome_empresa}}', '[Nome da Empresa]');
        }
        
        setFormData(prev => ({
          ...prev,
          [name]: value,
          subject: subject
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
      }
    }
  };

  const previewEmail = async () => {
    if (formData.templateId === 'custom') {
      toast({
        title: 'Preview not available',
        description: 'Preview is only available for predefined templates',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoadingPreview(true);
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/email/test/preview`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          templateId: formData.templateId,
          variables: formData.variables
        })
      });

      const data = await response.json();
      if (data.success) {
        setEmailPreview(data.preview);
      } else {
        toast({
          title: 'Preview failed',
          description: data.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error previewing email:', error);
      toast({
        title: 'Preview error',
        description: 'Failed to generate email preview',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const sendTestEmail = async () => {
    // Validate form
    if (!formData.to) {
      toast({
        title: 'Validation error',
        description: 'Please enter at least one recipient email',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Prepare recipients
    let recipients = [formData.to];
    if (formData.additionalEmails) {
      const additional = formData.additionalEmails
        .split(',')
        .map(email => email.trim())
        .filter(email => email);
      recipients = [...recipients, ...additional];
    }

    try {
      setSendingEmail(true);
      const token = getToken();
      
      const payload = {
        to: recipients,
        subject: formData.subject,
        templateId: formData.templateId,
        customHtml: formData.templateId === 'custom' ? formData.customHtml : undefined,
        variables: formData.variables,
        ccAdmin: formData.ccAdmin
      };

      const response = await fetch(`${API_BASE_URL}/email/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: 'Email sent successfully!',
          description: `Test email sent to ${recipients.join(', ')}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Add to test history
        setTestHistory(prev => [{
          ...data.details,
          status: 'success'
        }, ...prev].slice(0, 10));

        // Clear form
        setFormData(prev => ({
          ...prev,
          to: '',
          additionalEmails: '',
          subject: '',
          customHtml: ''
        }));
      } else {
        toast({
          title: 'Failed to send email',
          description: data.message,
          status: 'error',
          duration: 7000,
          isClosable: true,
        });

        // Add to test history
        setTestHistory(prev => [{
          recipients,
          timestamp: new Date().toISOString(),
          status: 'failed',
          error: data.message
        }, ...prev].slice(0, 10));
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Network error',
        description: 'Failed to connect to server',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const getDefaultHtmlTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { font-family: Arial, sans-serif; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #4A5568; color: white; padding: 20px; text-align: center; }
    .content { padding: 20px; background: #f7f7f7; }
    .footer { text-align: center; padding: 10px; color: #666; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>MICA CAPITAL</h1>
    </div>
    <div class="content">
      <h2>Olá!</h2>
      <p>Este é um email de teste.</p>
      <p>Se você recebeu este email, a configuração está funcionando corretamente!</p>
    </div>
    <div class="footer">
      <p>© 2025 MICA Capital. Todos os direitos reservados.</p>
    </div>
  </div>
</body>
</html>`;
  };

  return (
    <Container maxW="7xl" py={5}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>
            <Icon as={MdEmail} mr={2} />
            Testar Envio de Email
          </Heading>
          <Text color="gray.600">
            Teste a configuração do SendGrid enviando emails de teste
          </Text>
        </Box>

        {/* SendGrid Status Card */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">Status do SendGrid</Heading>
              <Button
                size="sm"
                leftIcon={<MdRefresh />}
                onClick={checkSendGridStatus}
                isLoading={loadingStatus}
              >
                Atualizar
              </Button>
            </HStack>
          </CardHeader>
          <CardBody>
            {loadingStatus ? (
              <Spinner />
            ) : sendGridStatus ? (
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <HStack>
                  <Icon
                    as={sendGridStatus.configured ? MdCheckCircle : MdError}
                    color={sendGridStatus.configured ? 'green.500' : 'red.500'}
                    boxSize={6}
                  />
                  <Box>
                    <Text fontWeight="bold">Configuração</Text>
                    <Text fontSize="sm" color="gray.600">
                      {sendGridStatus.configured ? 'Configurado' : 'Não configurado'}
                    </Text>
                  </Box>
                </HStack>

                <HStack>
                  <Icon
                    as={sendGridStatus.apiKeyPresent ? MdCheckCircle : MdWarning}
                    color={sendGridStatus.apiKeyPresent ? 'green.500' : 'orange.500'}
                    boxSize={6}
                  />
                  <Box>
                    <Text fontWeight="bold">API Key</Text>
                    <Text fontSize="sm" color="gray.600">
                      {sendGridStatus.apiKeyPresent ? 'Presente' : 'Ausente'}
                    </Text>
                  </Box>
                </HStack>

                <HStack>
                  <Icon
                    as={sendGridStatus.fromEmailConfigured ? MdCheckCircle : MdWarning}
                    color={sendGridStatus.fromEmailConfigured ? 'green.500' : 'orange.500'}
                    boxSize={6}
                  />
                  <Box>
                    <Text fontWeight="bold">Email Remetente</Text>
                    <Text fontSize="sm" color="gray.600">
                      {sendGridStatus.fromEmail || 'Não configurado'}
                    </Text>
                  </Box>
                </HStack>
              </SimpleGrid>
            ) : (
              <Alert status="warning">
                <AlertIcon />
                <AlertTitle>Status indisponível</AlertTitle>
                <AlertDescription>
                  Não foi possível verificar o status do SendGrid
                </AlertDescription>
              </Alert>
            )}
          </CardBody>
        </Card>

        {/* Email Form */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardBody>
            <Tabs>
              <TabList>
                <Tab>Compor Email</Tab>
                <Tab>Preview</Tab>
                <Tab>Histórico</Tab>
              </TabList>

              <TabPanels>
                {/* Compose Tab */}
                <TabPanel>
                  <VStack spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>Email do Destinatário</FormLabel>
                      <Input
                        type="email"
                        name="to"
                        value={formData.to}
                        onChange={handleInputChange}
                        placeholder="exemplo@email.com"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Emails Adicionais (separados por vírgula)</FormLabel>
                      <Input
                        name="additionalEmails"
                        value={formData.additionalEmails}
                        onChange={handleInputChange}
                        placeholder="email1@exemplo.com, email2@exemplo.com"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Assunto</FormLabel>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="Assunto do email de teste"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Template</FormLabel>
                      <Select
                        name="templateId"
                        value={formData.templateId}
                        onChange={handleInputChange}
                      >
                        <option value="custom">HTML Personalizado</option>
                        {templates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                            {template.id.includes('v3') ? ' (v3)' : ''}
                            {template.id.includes('v4') ? ' (v4)' : ''}
                            {template.id.includes('v5') ? ' (v5)' : ''}
                          </option>
                        ))}
                      </Select>
                    </FormControl>

                    {formData.templateId !== 'custom' ? (
                      <>
                        <FormControl>
                          <FormLabel>Nome (variável)</FormLabel>
                          <Input
                            name="variables.nome"
                            value={formData.variables.nome}
                            onChange={handleInputChange}
                            placeholder="João Silva"
                          />
                        </FormControl>

                        {formData.templateId === 'precatorio-notification-v3' ? (
                          <FormControl>
                            <FormLabel>Valor do Precatório (variável)</FormLabel>
                            <Input
                              name="variables.valor"
                              value={formData.variables.valor}
                              onChange={handleInputChange}
                              placeholder="1.500.000,00"
                            />
                          </FormControl>
                        ) : formData.templateId === 'precatorio-notification-v4' ? (
                          <>
                            <FormControl>
                              <FormLabel>Tipo/Órgão ou Nº do Processo (variável)</FormLabel>
                              <Input
                                name="variables.tipo_orgao_processo"
                                value={formData.variables.tipo_orgao_processo}
                                onChange={handleInputChange}
                                placeholder="TRT 2ª Região - Processo nº 123456"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Valor Estimado (variável)</FormLabel>
                              <Input
                                name="variables.valor_estimado"
                                value={formData.variables.valor_estimado}
                                onChange={handleInputChange}
                                placeholder="2.000.000,00"
                              />
                            </FormControl>
                          </>
                        ) : formData.templateId === 'precatorio-notification-v5' ? (
                          <>
                            <FormControl>
                              <FormLabel>Nome da Empresa (variável)</FormLabel>
                              <Input
                                name="variables.nome_empresa"
                                value={formData.variables.nome_empresa}
                                onChange={handleInputChange}
                                placeholder="Empresa ABC"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Razão Social (variável)</FormLabel>
                              <Input
                                name="variables.razao_social"
                                value={formData.variables.razao_social}
                                onChange={handleInputChange}
                                placeholder="Empresa ABC Ltda"
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Valor (variável)</FormLabel>
                              <Input
                                name="variables.valor"
                                value={formData.variables.valor}
                                onChange={handleInputChange}
                                placeholder="5.000.000,00"
                              />
                            </FormControl>
                          </>
                        ) : (
                          <>
                            <FormControl>
                              <FormLabel>Precatórios (variável)</FormLabel>
                              <Input
                                name="variables.precatorios"
                                value={formData.variables.precatorios}
                                onChange={handleInputChange}
                                placeholder="PREC-001, PREC-002"
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel>Conteúdo Personalizado (variável)</FormLabel>
                              <Textarea
                                name="variables.conteudo_personalizado"
                                value={formData.variables.conteudo_personalizado}
                                onChange={handleInputChange}
                                placeholder="Conteúdo adicional para o email..."
                                rows={3}
                              />
                            </FormControl>
                          </>
                        )}
                      </>
                    ) : (
                      <FormControl>
                        <FormLabel>HTML do Email</FormLabel>
                        <Textarea
                          name="customHtml"
                          value={formData.customHtml || getDefaultHtmlTemplate()}
                          onChange={handleInputChange}
                          placeholder="<html>...</html>"
                          rows={10}
                          fontFamily="monospace"
                          fontSize="sm"
                        />
                      </FormControl>
                    )}

                    <FormControl>
                      <HStack>
                        <Switch
                          id="ccAdmin"
                          name="ccAdmin"
                          isChecked={formData.ccAdmin}
                          onChange={handleInputChange}
                        />
                        <FormLabel htmlFor="ccAdmin" mb={0}>
                          Enviar cópia para meu email
                        </FormLabel>
                      </HStack>
                    </FormControl>

                    <Divider />

                    <HStack justify="space-between">
                      <Button
                        leftIcon={<MdVisibility />}
                        onClick={previewEmail}
                        isLoading={loadingPreview}
                        isDisabled={formData.templateId === 'custom'}
                      >
                        Preview
                      </Button>
                      <Button
                        colorScheme="blue"
                        leftIcon={<MdSend />}
                        onClick={sendTestEmail}
                        isLoading={sendingEmail}
                        loadingText="Enviando..."
                      >
                        Enviar Email de Teste
                      </Button>
                    </HStack>
                  </VStack>
                </TabPanel>

                {/* Preview Tab */}
                <TabPanel>
                  {emailPreview ? (
                    <VStack align="stretch" spacing={4}>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Assunto:</Text>
                        <Code p={2} w="full">{emailPreview.subject}</Code>
                      </Box>
                      <Box>
                        <Text fontWeight="bold" mb={2}>Conteúdo:</Text>
                        <iframe
                          srcDoc={emailPreview.html}
                          style={{ 
                            width: '100%', 
                            height: '600px', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            backgroundColor: 'white'
                          }}
                          title="Email Preview"
                        />
                      </Box>
                    </VStack>
                  ) : (
                    <Alert status="info">
                      <AlertIcon />
                      <AlertTitle>Nenhum preview disponível</AlertTitle>
                      <AlertDescription>
                        Selecione um template e clique em "Preview" para visualizar
                      </AlertDescription>
                    </Alert>
                  )}
                </TabPanel>

                {/* History Tab */}
                <TabPanel>
                  {testHistory.length > 0 ? (
                    <VStack align="stretch" spacing={3}>
                      {testHistory.map((test, index) => (
                        <Box
                          key={index}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          borderColor={test.status === 'success' ? 'green.200' : 'red.200'}
                          bg={test.status === 'success' ? 'green.50' : 'red.50'}
                        >
                          <HStack justify="space-between">
                            <HStack>
                              <Icon
                                as={test.status === 'success' ? MdCheckCircle : MdError}
                                color={test.status === 'success' ? 'green.500' : 'red.500'}
                              />
                              <Text fontWeight="bold">
                                {test.recipients?.join(', ') || 'Email de teste'}
                              </Text>
                            </HStack>
                            <Badge
                              colorScheme={test.status === 'success' ? 'green' : 'red'}
                            >
                              {test.status}
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            {new Date(test.timestamp).toLocaleString('pt-BR')}
                          </Text>
                          {test.subject && (
                            <Text fontSize="sm" mt={1}>
                              Assunto: {test.subject}
                            </Text>
                          )}
                          {test.error && (
                            <Text fontSize="sm" color="red.600" mt={1}>
                              Erro: {test.error}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Alert status="info">
                      <AlertIcon />
                      <AlertTitle>Nenhum teste realizado</AlertTitle>
                      <AlertDescription>
                        Os emails de teste enviados aparecerão aqui
                      </AlertDescription>
                    </Alert>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>

        {/* Help Section */}
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <Heading size="md">
              <Icon as={MdInfo} mr={2} />
              Informações Úteis
            </Heading>
          </CardHeader>
          <CardBody>
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontWeight="bold" mb={1}>Variáveis Disponíveis nos Templates:</Text>
                <List spacing={1}>
                  <ListItem>
                    <ListIcon as={MdCheck} color="green.500" />
                    <Code>{'{{nome}}'}</Code> - Nome do destinatário
                  </ListItem>
                  <ListItem>
                    <ListIcon as={MdCheck} color="green.500" />
                    <Code>{'{{precatorios}}'}</Code> - Lista de precatórios
                  </ListItem>
                  <ListItem>
                    <ListIcon as={MdCheck} color="green.500" />
                    <Code>{'{{conteudo_personalizado}}'}</Code> - Conteúdo personalizado
                  </ListItem>
                </List>
              </Box>
              
              <Divider />
              
              <Box>
                <Text fontWeight="bold" mb={1}>Dicas:</Text>
                <List spacing={1}>
                  <ListItem fontSize="sm">
                    • Teste primeiro com seu próprio email
                  </ListItem>
                  <ListItem fontSize="sm">
                    • Use templates pré-definidos para manter consistência
                  </ListItem>
                  <ListItem fontSize="sm">
                    • Verifique o spam se não receber o email
                  </ListItem>
                  <ListItem fontSize="sm">
                    • O limite é de 10 emails de teste por minuto
                  </ListItem>
                </List>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Container>
  );
};

export default EmailTest;